/**
 * Shared per-customer_type Basic Information config (Wave D1b), reused by
 * the Contacts form (CustomerFormPage) and the Policy Create page's minimal
 * Policy Holder subset (Wave E) so both stay in lockstep with the backend's
 * Customer.CUSTOMER_TYPE_CHOICES / SALUTATION_CHOICES
 * (backend/apps/customers/models.py).
 */

// Only Individual/Corporate/Proprietorship have a dedicated Basic-Info field
// config (Phase 1). HUF/Trust/Partnership/LLP are a documented Phase-2
// extension point -- see CustomerFormPage's BASIC_FIELDS_BY_TYPE comment.
export const PHASE1_TYPES = ['individual', 'corporate', 'proprietorship'] as const;
export type Phase1Type = (typeof PHASE1_TYPES)[number];

export function isPhase1Type(type: string | undefined): type is Phase1Type {
  return (PHASE1_TYPES as readonly string[]).includes(type ?? '');
}

export const customerTypeOptions = [
  { value: 'individual', label: 'Individual' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'huf', label: 'HUF' },
  { value: 'trust', label: 'Trust' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llp', label: 'LLP' },
  { value: 'proprietorship', label: 'Proprietorship' },
];

// Section 2 of the Wave D1b brief: salutation options vary by customer_type
// (codes match Customer.SALUTATION_CHOICES on the backend).
export const SALUTATION_LABELS: Record<string, string> = {
  mr: 'Mr.',
  mrs: 'Mrs.',
  ms: 'Ms.',
  miss: 'Miss',
  dr: 'Dr.',
  prof: 'Prof.',
  shri: 'Shri',
  smt: 'Smt.',
  mx: 'Mx.',
  ms_business: 'M/s',
  company: 'Company',
  organization: 'Organization',
  huf: 'HUF',
  trust: 'Trust',
  llp: 'LLP',
};

export const SALUTATION_CODES_BY_TYPE: Partial<Record<string, string[]>> = {
  individual: ['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'shri', 'smt', 'mx'],
  corporate: ['ms_business', 'company', 'organization'],
  proprietorship: ['mr', 'mrs', 'ms', 'ms_business'],
  // Phase 2 extension point -- Basic/Identity config for these types isn't
  // built yet (they fall back to Individual), but the salutation map is
  // cheap to define now since the backend choices already exist.
  huf: ['huf', 'ms_business'],
  trust: ['trust', 'ms_business'],
  partnership: ['ms_business'],
  llp: ['ms_business', 'llp'],
};

export function salutationCodesFor(customerType: string | undefined): string[] {
  return SALUTATION_CODES_BY_TYPE[customerType ?? ''] ?? [];
}

export function salutationOptionsFor(customerType: string | undefined) {
  return salutationCodesFor(customerType).map((value) => ({
    value,
    label: SALUTATION_LABELS[value] ?? value,
  }));
}

// ── Identity & Tax fields (Wave D1b, BROKER-3) ──────────────────────────────
// The per-customer_type Identity & Tax field schema, shared by the Contacts
// form (CustomerFormPage renders an input per key) and the Contact detail page
// (CustomerDetailPage's KYC card renders a read-only value per key). Keeping
// this here -- not in CustomerFormPage -- is what lets the detail page's KYC
// card show the SAME per-type identity set the form captured, instead of a
// hardcoded PAN/Aadhaar/GST trio for every customer_type.
export type IdentityFieldKey =
  | 'pan_number'
  | 'aadhaar_number'
  | 'gst_number'
  | 'passport_number'
  | 'driving_license';

export const IDENTITY_FIELDS_BY_TYPE: Record<Phase1Type, IdentityFieldKey[]> = {
  individual: ['pan_number', 'aadhaar_number', 'passport_number', 'driving_license'],
  corporate: ['gst_number', 'pan_number'],
  proprietorship: ['pan_number', 'gst_number'],
};

// HUF/Trust/Partnership/LLP fall back to the Individual identity set (same as
// the Basic-Info config) until Phase 2 gives them a dedicated one.
export function getIdentityFields(type: string | undefined): IdentityFieldKey[] {
  return isPhase1Type(type) ? IDENTITY_FIELDS_BY_TYPE[type] : IDENTITY_FIELDS_BY_TYPE.individual;
}

export const IDENTITY_FIELD_LABELS: Record<IdentityFieldKey, string> = {
  pan_number: 'PAN Number',
  aadhaar_number: 'Aadhaar Number',
  gst_number: 'GST Number',
  passport_number: 'Passport Number',
  driving_license: 'Driving License',
};

// Types whose core identifying "name" is a company/business name rather than
// a person's first/last name. Wave E's minimal Policy Holder subset
// (PolicyCreatePage) uses this to swap its single Name input's label between
// "First Name" and "Company/Business Name". CustomerFormPage's full form
// instead shows a dedicated `company_name` field for these types (see its
// BASIC_FIELDS_BY_TYPE) alongside first/last name for Proprietorship.
export function isCompanyNameType(customerType: string | undefined): boolean {
  return customerType === 'corporate' || customerType === 'proprietorship';
}

// ── Basic Information fields (Wave D1b, moved here 2026-09-02) ──────────────
// The per-customer_type Basic Information schema. It used to live inside
// CustomerFormPage; it moved here so other pages (#272 Contacts detail, #273
// Contacts import/export) can render/validate the SAME per-type field set the
// Contacts form captures instead of re-deriving it.
//
// Only Individual/Corporate/Proprietorship are built out (Phase 1). HUF/Trust/
// Partnership/LLP are a documented Phase-2 extension point: their Customer
// Type option remains selectable, but BASIC_FIELDS_BY_TYPE/IDENTITY_FIELDS_BY_TYPE
// fall back to the Individual field set (getBasicFields/getIdentityFields) so
// those types stay fully usable -- not blank/broken -- until Phase 2 gives them
// a dedicated config.

// Every field that can appear/disappear across Basic Information + Identity &
// Tax depending on customer_type. customer_type/segment are NOT here -- they
// render unconditionally for every type.
export type CustomerFieldKey =
  | 'salutation'
  | 'first_name'
  | 'middle_name'
  | 'last_name'
  | 'date_of_birth'
  | 'gender'
  | 'marital_status'
  | 'occupation'
  | 'annual_income'
  | 'company_name'
  | 'cin_number'
  | 'incorporation_date'
  | 'industry'
  | 'nature_of_business'
  | 'annual_turnover'
  | 'num_employees'
  | 'website'
  | 'company_type'
  | 'pan_number'
  | 'aadhaar_number'
  | 'gst_number'
  | 'passport_number'
  | 'driving_license';

// Section 1 of the Wave D1b brief, field-for-field. NOTE two deliberate
// additions beyond the brief's literal per-type lists, both to avoid a field
// silently vanishing when a broker switches Customer Type mid-form:
//  - 'salutation' is added to Corporate's Basic list. Section 2 defines real
//    salutation options for Corporate (M/s, Company, Organization), so
//    omitting the input entirely would make those choices unreachable.
//  - Proprietorship keeps 'segment' (rendered unconditionally for every type)
//    even though section 1's literal Proprietorship list omits it.
export const BASIC_FIELDS_BY_TYPE: Record<Phase1Type, CustomerFieldKey[]> = {
  individual: [
    'salutation',
    'first_name',
    'middle_name',
    'last_name',
    'date_of_birth',
    'gender',
    'marital_status',
    'occupation',
    'annual_income',
  ],
  corporate: [
    'salutation',
    'company_name',
    'cin_number',
    'incorporation_date',
    'industry',
    'nature_of_business',
    'annual_turnover',
    'num_employees',
    'website',
  ],
  proprietorship: [
    'company_name',
    'first_name',
    'last_name',
    'salutation',
    'incorporation_date',
    'company_type',
    'annual_turnover',
    'date_of_birth',
    'gender',
  ],
};

export function getBasicFields(type: string | undefined): CustomerFieldKey[] {
  return isPhase1Type(type) ? BASIC_FIELDS_BY_TYPE[type] : BASIC_FIELDS_BY_TYPE.individual;
}

// ── Field metadata ─────────────────────────────────────────────────────────
// Transcribed field-for-field from CustomerFormPage's renderField() and its
// zod superRefine, so a consumer that renders (or validates, or column-heads a
// CSV) from these specs produces exactly what the Contacts form produces.

export const segmentOptions = [
  { value: 'standard', label: 'Standard' },
  { value: 'premium', label: 'Premium' },
  { value: 'high_value', label: 'High Value' },
  { value: 'vip', label: 'VIP' },
  { value: 'corporate', label: 'Corporate' },
];

export const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

export const maritalStatusOptions = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
];

export type CustomerFieldInputType = 'text' | 'date' | 'number' | 'url' | 'select';

export interface CustomerFieldSpec {
  key: CustomerFieldKey;
  /** Label as the Contacts form renders it, already resolved for the type. */
  label: string;
  inputType: CustomerFieldInputType;
  /** Matches CustomerFormPage's zod superRefine for this customer_type. */
  required: boolean;
  /** Select fields only. */
  options?: { value: string; label: string }[];
  placeholder?: string;
  maxLength?: number;
  inputMode?: 'numeric';
  /** Non-digit keystrokes are rejected (aadhaar_number). */
  digitsOnly?: boolean;
  min?: number;
  step?: number;
}

/** The label the Contacts form shows for `key` under `customerType`. */
export function customerFieldLabel(key: CustomerFieldKey, customerType: string | undefined): string {
  switch (key) {
    case 'salutation':
      return 'Salutation';
    case 'first_name':
      return customerType === 'proprietorship' ? 'Proprietor Name' : 'First Name';
    case 'middle_name':
      return 'Middle Name';
    case 'last_name':
      return customerType === 'proprietorship' ? "Proprietor's Last Name" : 'Last Name';
    case 'date_of_birth':
      return 'Date of Birth';
    case 'gender':
      return 'Gender';
    case 'marital_status':
      return 'Marital Status';
    case 'occupation':
      return 'Occupation';
    case 'annual_income':
      return 'Annual Income';
    case 'company_name':
      return customerType === 'corporate' ? 'Company Name' : 'Business Name';
    case 'cin_number':
      return 'CIN';
    case 'incorporation_date':
      return customerType === 'corporate' ? 'Date of Incorporation' : 'Date of Establishment';
    case 'industry':
      return 'Industry';
    case 'nature_of_business':
      return 'Nature of Business';
    case 'annual_turnover':
      return 'Annual Turnover';
    case 'num_employees':
      return 'Number of Employees';
    case 'website':
      return 'Website';
    case 'company_type':
      return 'Business Type';
    default:
      return IDENTITY_FIELD_LABELS[key as IdentityFieldKey] ?? key;
  }
}

function baseFieldSpec(key: CustomerFieldKey, customerType: string | undefined): CustomerFieldSpec {
  const label = customerFieldLabel(key, customerType);
  switch (key) {
    case 'salutation':
      return { key, label, inputType: 'select', required: false, options: salutationOptionsFor(customerType) };
    case 'gender':
      return { key, label, inputType: 'select', required: false, options: genderOptions };
    case 'marital_status':
      return { key, label, inputType: 'select', required: false, options: maritalStatusOptions };
    case 'date_of_birth':
    case 'incorporation_date':
      return { key, label, inputType: 'date', required: false };
    case 'annual_income':
    case 'annual_turnover':
      return { key, label, inputType: 'number', required: false, inputMode: 'numeric' };
    case 'num_employees':
      return { key, label, inputType: 'number', required: false, inputMode: 'numeric', min: 0, step: 1 };
    case 'website':
      return { key, label, inputType: 'url', required: false, placeholder: 'https://example.com' };
    case 'cin_number':
      return { key, label, inputType: 'text', required: false, placeholder: 'U12345MH2020PLC123456', maxLength: 21 };
    case 'pan_number':
      return { key, label, inputType: 'text', required: false, placeholder: 'ABCDE1234F', maxLength: 10 };
    case 'aadhaar_number':
      return { key, label, inputType: 'text', required: false, maxLength: 12, inputMode: 'numeric', digitsOnly: true };
    case 'gst_number':
      return { key, label, inputType: 'text', required: false, placeholder: '22ABCDE1234F1Z5', maxLength: 15 };
    case 'passport_number':
    case 'driving_license':
      return { key, label, inputType: 'text', required: false, maxLength: 20 };
    default:
      return { key, label, inputType: 'text', required: false };
  }
}

/**
 * The Basic Information fields for `customerType`, in the order the Contacts
 * form renders them, each with the label/input type/required flag that form
 * uses. `required` mirrors CustomerFormPage's superRefine exactly: first_name
 * for every type but corporate; last_name and company_name whenever they are
 * visible for the type.
 */
export function getBasicFieldSpecs(customerType: string | undefined): CustomerFieldSpec[] {
  const keys = getBasicFields(customerType);
  const isCorporate = customerType === 'corporate';
  return keys.map((key) => {
    const spec = baseFieldSpec(key, customerType);
    if (key === 'first_name') return { ...spec, required: !isCorporate };
    if (key === 'last_name' || key === 'company_name') return { ...spec, required: true };
    return spec;
  });
}

/**
 * The Identity & Tax fields for `customerType`, same shape as
 * getBasicFieldSpecs. None of them are required by the Contacts form.
 */
export function getIdentityFieldSpecs(customerType: string | undefined): CustomerFieldSpec[] {
  return getIdentityFields(customerType).map((key) => baseFieldSpec(key, customerType));
}
